import { forwardRef } from 'react';
import type { Ref } from 'react';

import type { TextInput as RNTextInput } from 'react-native';

import { TextInputBase } from './shared/TextInputBase';
import type { TextInputBaseProps } from './shared/TextInputBase';

type TextInputProps = TextInputBaseProps & {
  /** The multiline property is excluded in this type */
  multiline?: never;
};

/**
 * Use TextInput to collect single-line text from the user. For multiline text,
 * use TextArea.
 *
 * If you want validation to be handled for you, use ValidatedTextInput instead
 * (it's a controlled input component that validates & formats for you).
 *
 * TextInput always takes up the full width of its container. To limit its
 * width, wrap it in a Box or other container component.
 *
 * To use TextInput as an uncontrolled component, leave the `value` prop
 * undefined. To use it as a controlled component, pass `value` and
 * `onChangeText`. If you need to prefill the input but still want to use an
 * uncontrolled component, use the `prefilledValue` prop.
 *
 * You must pass a `title` prop. This is so we can ensure that the input meets
 * accessibility requirements for screen readers.
 * In the rare case that you can't pass a `title` prop, make sure to pass `ariaLabel` or `ariaLabelledBy` instead.
 *
 * If you want to track impression events ("inputViewEvent"), pass
 * `analyticsName` and `trackImpression`.
 *
 * TextInput supports a subset of [React Native's TextInput props](
 * https://reactnative.dev/docs/textinput). One notable prop is [autoComplete](
 * https://reactnative.dev/docs/textinput#autocomplete) — make sure to include
 * `autoComplete` whenever relevant so the user's device can offer useful
 * suggestions for filling out the input. Another prop to know about is
 * [autoFocus](https://reactnative.dev/docs/textinput#autofocus) — be careful
 * when using `autoFocus` because it can cause confusing behavior. Automatically
 * focusing on a control can make the page scroll on load and cause users to
 * miss context created by content earlier on the page. It can also cause
 * dynamic keyboards to display over the page on touch devices. For
 * screen-readers, it can "teleport" the user to the form control without
 * warning them or announcing anything on the page before the control's label.
 */
const TextInput = forwardRef(function TextInput(props: TextInputProps, ref: Ref<RNTextInput>) {
  return <TextInputBase {...props} ref={ref} />;
});
export { TextInput };
export type { TextInputProps };
